<template>
  <section class="handyrechnung-stores-hero">
    <div class="handyrechnung-stores-hero__content">
      <Heading
        tag="h1"
        size="lg"
        class="handyrechnung-stores-hero__title"
      >
        100 % Cashback <DcbFootnote /><br />
        <span class="text--primary">auf deinen 1. In-App-Kauf.</span>
      </Heading>
      <Paragraph class="handyrechnung-stores-hero__sub">
        Apps, Abos und In-App-Käufe im
        <span class="handyrechnung-stores-hero__store-name">
          <CdnImage
            src="/content/content-images/dcb/stores/54becf0ade1d949e0b42df8bd4f78381/logo-appstore.svg"
            alt="App Store"
            aspect-ratio="1"
            sizes="xs:24px"
            lazy
            render-without-figure
          />
          App Store
        </span>
        und
        <span class="handyrechnung-stores-hero__store-name">
          <CdnImage
            src="/content/content-images/dcb/stores/a142233a0b9d83cb884c8f949ee2f420/logo-googleplay.svg"
            alt="Google Play"
            aspect-ratio="1"
            sizes="xs:24px"
            lazy
            render-without-figure
          />
          Google Play Store</span
        >
        per Telekom Handyrechnung zahlen. Ohne Karte, ohne Bankkonto. Für deinen ersten Kauf gibt es
        bis zu 10&nbsp;€ zurück.
      </Paragraph>
      <ul class="handyrechnung-stores-hero__checks">
        <li>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect
              x="2"
              y="5"
              width="20"
              height="14"
              rx="2"
            />
            <path d="M2 10h20M3 3l18 18" /></svg
          >Ohne Kreditkarte
        </li>
        <li>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3 4 6v6c0 4.4 3.4 8.2 8 9 4.6-.8 8-4.6 8-9V6l-8-3z" />
            <path d="m9 12.2 2 2 4-4.4" /></svg
          >100 % zurück (bis 10&nbsp;€)
        </li>
        <li>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
            />
            <polyline points="12 7 12 12 15.5 14" /></svg
          >In unter 2 Minuten
        </li>
      </ul>

      <div class="handyrechnung-stores-hero__apps">
        <div class="handyrechnung-stores-hero__apps-label">Zum Beispiel bei</div>
        <span class="handyrechnung-stores-hero__apps-icons">
          <span
            v-for="app in exampleApps"
            :key="app.alt"
            class="handyrechnung-stores-hero__app"
          >
            <CdnImage
              class="handyrechnung-stores-hero__apps-icon"
              :src="app.src"
              :alt="app.alt"
              aspect-ratio="1"
              sizes="xs:40px"
              render-without-figure
              lazy
            />
            {{ app.alt }}
          </span>
        </span>
      </div>

      <div class="handyrechnung-stores-hero__actions">
        <LinkButton
          tag="button"
          type="button"
          variant="primary"
          size="medium"
          @click="scrollToTarget('mail-form')"
        >
          Cashback sichern
        </LinkButton>
        <LinkButton
          tag="button"
          type="button"
          variant="outline-primary"
          class="handyrechnung-stores-hero__actions--secondary"
          size="medium"
          @click="scrollToTarget('wofuer')"
        >
          So richtest du's ein
        </LinkButton>
      </div>
    </div>
    <div class="handyrechnung-stores-hero__image-container">
      <Badge
        class="handyrechnung-stores-hero__badge"
        default-font-size="11px"
        magenta
      >
        <span style="white-space: nowrap">
          Bis zu<br />
          <span style="font-size: 1.6rem"> 10&nbsp;€ <DcbFootnote /> </span><br />
          Cashback
        </span>
      </Badge>
      <CdnImage
        class="handyrechnung-stores-hero__image"
        src="/content/content-images/dcb/stores/33c8e8cbd51deb2d0efb78c53766a33e/hero.jpg"
        aspect-ratio="540 / 731"
        sizes="xs:100vw md:50vw lg:640px"
        render-without-figure
        alt="Junge Frau nutzt ihr Smartphone in der Stadt"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { Heading, Paragraph, LinkButton, Badge } from '@i22team/td-longtail-vue-components';
import CdnImage from '~/components/cdn-image.vue';
import DcbFootnote from '~/components/dcb/stores/dcb-footnote.vue';

const exampleApps = [
  {
    src: '/content/content-images/dcb/stores/app-icons/fadd8b90d29e89d5e2a313600c90001e/icon-youtube.png',
    alt: 'YouTube Premium',
  },
  {
    src: '/content/content-images/dcb/stores/app-icons/0c7c93524bb12d64f02c8174b16e10e1/icon-chatgpt.svg',
    alt: 'ChatGPT',
  },
  {
    src: '/content/content-images/dcb/stores/app-icons/982f398cb40fcc95a046eabdb4c06948/icon-endel.png',
    alt: 'Endel',
  },
  {
    src: '/content/content-images/dcb/stores/app-icons/5ae44c3002f3294ddb3b1e2dc90052d5/icon-audioteka.png',
    alt: 'Audioteka',
  },
];

const scrollToTarget = (target: string) => {
  document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.handyrechnung-stores-hero {
  display: flex;
  background: var(--color-gray-100);
  position: relative;
  gap: 1rem;
  flex-direction: column-reverse;

  @include mq($mq-sm) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    align-items: stretch;
    gap: 2rem;
  }

  &__image-container {
    position: relative;
    min-height: clamp(290px, 68vw, 380px);

    @include mq($mq-md) {
    }
  }

  &__badge {
    z-index: 1;
    --badge-xs-top: 70%;
    --badge-xs-left: 70%;
    --badge-sm-bottom: auto;
    --badge-sm-top: 45%;
    --badge-sm-left: -5%;
    filter: drop-shadow(rgba(0, 0, 0, 0.25) 0px 8px 20px);
    animation: badge-pulse 2.5s ease-in-out infinite;
  }

  @keyframes badge-pulse {
    0%,
    100% {
      transform: rotate(-6deg) scale(1);
    }
    50% {
      transform: rotate(-6deg) scale(1.06);
    }
  }

  &__image {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__content {
    margin-left: auto;
    padding-block: 4rem 3.5rem;
    padding-inline: $grid-base;

    @include mq($mq-md) {
      max-width: 552px;
    }
  }

  &__title {
    margin-bottom: 1rem;
  }

  &__sub {
    margin-bottom: 1.5rem;

    > img {
      max-height: 24px;
    }
  }

  &__store-name {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    line-height: 1;
    font-size: 0.9em;
    box-shadow: rgba(16, 12, 24, 0.05) 0 1px 2px;
    gap: 0.3em;
    border-radius: 7px;
    padding: calc(0.12em + 2px) 0.42em;
    margin: 0 0.08em;
    white-space: nowrap;
    background-color: var(--color-bg);
    border: 1px solid #e0e0e0;
    font-weight: 700;

    img {
      height: 0.95em;
      width: auto;
      max-width: 1.5em;
      object-fit: contain;
      display: block;
      align-self: center;
    }
  }

  &__checks {
    list-style: none;
    padding: 0;
    margin: 0 0 1.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;

      > svg {
        color: var(--color-primary);
        width: 22px;
      }
    }
  }

  &__apps {
    margin-bottom: 1.5rem;

    @include mq($mq-md) {
      margin-bottom: 3rem;
    }
  }

  &__apps-label {
    font-size: 14px;
    text-transform: uppercase;
    color: var(--color-font-subtle);
    margin-bottom: 1rem;
  }

  &__apps-icons {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__app {
    box-shadow: 0 2px 6px rgba(16, 12, 24, 0.12);
    border: 1px solid var(--color-gray-200);
    border-radius: 999px;
    font-size: 0.9rem;

    display: flex;
    align-items: center;
    gap: 0.5rem;
    background-color: var(--color-bg);
    padding: 0.3rem 0.75rem 0.3rem 0.35rem;

    flex: 0 0 calc(50% - 0.5rem);
    @include mq($mq-md) {
      flex: 0 1 auto;
    }
  }

  &__apps-icon {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    object-fit: cover;
    display: block;
    flex: 0 0 auto;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 1.5rem;
    justify-content: center;
    @include mq($mq-md) {
      justify-content: flex-start;
    }

    &--secondary {
      --btn-color-background: #fff;
    }
  }
}
</style>
